import { supabase } from './supabase';
import type { Notification } from '../hooks/useNotifications';
import { reportError } from './errors';

export type BroadcastSeverity = 'info' | 'warning' | 'critical';

export interface Broadcast {
  id: string;
  title: string;
  message: string;
  severity: BroadcastSeverity;
  is_active: boolean;
  starts_at: string | null;
  ends_at: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

const BROADCAST_COLS =
  'id,title,message,severity,is_active,starts_at,ends_at,created_by,created_at,updated_at';

// Prefix used to distinguish broadcast pseudo-notifications from real rows.
export const BROADCAST_NOTIFICATION_PREFIX = 'broadcast:';

/** Active broadcasts currently within their (optional) start/end window. */
export async function fetchActiveBroadcasts(): Promise<Broadcast[]> {
  const { data, error } = await supabase
    .from('broadcasts')
    .select(BROADCAST_COLS)
    .eq('is_active', true)
    .order('created_at', { ascending: false });
  if (error) {
    console.error('[broadcasts] fetch', error.message);
    return [];
  }
  const now = Date.now();
  return (data as Broadcast[]).filter((b) => {
    const started = !b.starts_at || new Date(b.starts_at).getTime() <= now;
    const notEnded = !b.ends_at || new Date(b.ends_at).getTime() >= now;
    return started && notEnded;
  });
}

// ─── Admin CRUD (platform admins only; enforced by RLS) ─────────────────────
export interface BroadcastInput {
  title: string;
  message: string;
  severity: BroadcastSeverity;
  is_active: boolean;
  starts_at: string | null;
  ends_at: string | null;
}

export async function listAllBroadcasts(): Promise<Broadcast[]> {
  const { data, error } = await supabase
    .from('broadcasts')
    .select(BROADCAST_COLS)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data as Broadcast[]) ?? [];
}

export async function createBroadcast(input: BroadcastInput): Promise<Broadcast> {
  const { data: userData, error: userDataError } = await supabase.auth.getUser();
  if (userDataError) reportError(userDataError, 'broadcasts.auth-get-user');
  const { data, error } = await supabase
    .from('broadcasts')
    .insert({ ...input, created_by: userData.user?.id ?? null })
    .select(BROADCAST_COLS)
    .single();
  if (error) throw error;
  return data as Broadcast;
}

export async function setBroadcastActive(id: string, is_active: boolean): Promise<void> {
  const { error } = await supabase.from('broadcasts').update({ is_active }).eq('id', id);
  if (error) throw error;
}

export async function deleteBroadcast(id: string): Promise<void> {
  const { error } = await supabase.from('broadcasts').delete().eq('id', id);
  if (error) throw error;
}

// ─── Per-user dismiss state (localStorage; broadcasts aren't per-user rows) ──
const DISMISS_KEY = 'fd_dismissed_broadcasts';

function readDismissed(): Record<string, boolean> {
  try {
    return JSON.parse(localStorage.getItem(DISMISS_KEY) || '{}');
  } catch {
    return {};
  }
}

export function isBroadcastDismissed(id: string): boolean {
  return !!readDismissed()[id];
}

export function dismissBroadcast(id: string): void {
  const map = readDismissed();
  map[id] = true;
  try {
    localStorage.setItem(DISMISS_KEY, JSON.stringify(map));
  } catch {
    /* ignore quota errors */
  }
}

const SEVERITY_TO_TYPE: Record<BroadcastSeverity, Notification['type']> = {
  info: 'info',
  warning: 'warning',
  critical: 'error',
};

/** Map a broadcast to the Notification shape so it can render in the feed. */
export function broadcastToNotification(b: Broadcast): Notification {
  return {
    id: `${BROADCAST_NOTIFICATION_PREFIX}${b.id}`,
    org_id: '',
    user_id: null,
    title: b.title,
    message: b.message,
    type: SEVERITY_TO_TYPE[b.severity] ?? 'info',
    is_read: isBroadcastDismissed(b.id),
    metadata: { broadcast: true },
    created_at: b.created_at,
    read_at: null,
  };
}

export function isBroadcastNotificationId(id: string): boolean {
  return id.startsWith(BROADCAST_NOTIFICATION_PREFIX);
}

/** Extract the raw broadcast id from a `broadcast:<uuid>` notification id. */
export function broadcastIdFromNotificationId(id: string): string {
  return id.slice(BROADCAST_NOTIFICATION_PREFIX.length);
}
